<template>
  <div class="v-billing-select__wrapper" :class="{ ['opened']: isOpened }">
    <div class="current-item box" ref="currentItem" @click="_toggleOpen">
      <div v-if="isOpened" class="white-overflow"></div>
      <div v-if="_currentValue" class="item">
        <div class="icon">
          <Icon :icon="_currentValue.iconSrc"></Icon>
        </div>

        <div v-if="!_currentValue.title" class="value">
          <div class="title">
            {{ _currentValue.header }}
          </div>

          <div class="text">
            <span>
              {{ _currentValue.text }}
            </span>
          </div>
        </div>

        <div v-else>
          <div class="single-title">
            <span>{{ _currentValue.title }}</span>
            <span v-if="_currentValue.additionalTitle" class="additionalTitle">
              {{ _currentValue.additionalTitle }}
            </span>
          </div>
        </div>
      </div>

      <div class="chevron" @click="_toggleOpen">
        <i :class="_toggleIcon"></i>
      </div>
    </div>

    <div v-if="isOpened" class="opts-list box" ref="optsList">
      <ul>
        <li v-for="opt in options">
          <div
            class="item-left item-left__wrapper"
            @click="e => _onSelect(e, opt)"
          >
            <div class="radio-wrapper">
              <VRadioInput
                :checked="_optionCheck(opt.key)"
                :value="opt.key"
                @input="(v, e) => _onSelect(e, opt)"
              ></VRadioInput>
            </div>
            <div
              class="item-inner-wrapper"
              :class="{ active: _optionCheck(opt.key) }"
            >
              <div class="icon">
                <Icon :icon="opt.iconSrc"></Icon>
              </div>
              <div v-if="!opt.title" class="value">
                <div class="title">
                  {{ opt.header }}
                </div>
                <div class="text">
                  <span>F
                    {{ opt.text }}
                  </span>
                </div>
              </div>

              <div v-else class="single-title">
                <span>{{ opt.title }}</span>
                <span v-if="opt.additionalTitle" class="additionalTitle">
                  {{ opt.additionalTitle }}
                </span>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import withToasted from "@/mixins/withToasted";

import VRadioInput from "@/components/VRadioInput";
import WithLoader from "@/components/WithLoader";

import postcardIcon from "@/assets/images/postcard-icon.svg";
import deleteIcon from "@/assets/images/Delete.svg";

const Icon = Vue.component("Icon", {
  props: ["icon"],

  template: `
        <div class="icon-wrapper">
          <img :src="icon" alt="select-logo" />
        </div>
  `
});

export default Vue.component("VRadioSelect", {
  props: {
    options: VueTypes.arrayOf(
      VueTypes.shape({
        key: VueTypes.string,
        value: VueTypes.any,
        text: VueTypes.string,
        header: VueTypes.string,
        iconSrc: VueTypes.any,
        title: VueTypes.string,
        additionalTitle: VueTypes.string
      })
    ),

    selectedOption: VueTypes.any
  },

  components: {
    Icon,
    VRadioInput,
    WithLoader
  },

  mixins: [withToasted],

  data() {
    return {
      isOpened: false,
      deleteIcon,
      postcardIcon
    };
  },

  methods: {
    _optionCheck(id) {
      return id === this._selected;
    },

    _toggleOpen(e) {
      e.stopPropagation();
      this.isOpened = !this.isOpened;
    },

    _onSelect(e, opt) {
      e.stopPropagation();
      this.$emit("select", opt.key);
      this._toggleOpen(e);
    },

    handleDocumentClick(e) {
      e.stopPropagation();

      const { currentItem, optsList } = this.$refs;
      const target = e.target;

      if (
        this.isOpened &&
        target !== currentItem &&
        target !== optsList &&
        !optsList.contains(target)
      ) {
        this._toggleOpen(e);
      }
    }
  },

  computed: {
    _currentValue() {
      return this.options.filter(item => item.key === this._selected)[0];
    },

    _selected() {
      return this.selectedOption || this.options[0].key;
    },

    _toggleIcon() {
      return this.isOpened ? "fal fa-times" : "fa fa-chevron-down";
    }
  },

  created() {
    document.addEventListener("click", this.handleDocumentClick);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-billing-select";

.#{$ns} {
  &__wrapper {
    position: relative;

    .white-overflow {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.8);
    }

    .box {
      border-radius: 5px;
      background: $white;
      box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.1);
    }

    .item {
      display: flex;
      align-items: center;
    }

    .icon {
      margin-right: 10px;
    }

    .current-item {
      cursor: pointer;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.1);
      padding: 13px 15px;

      &:hover {
        .chevron {
          i {
            color: $primary-text-color;
          }
        }
      }

      .chevron {
        position: absolute;
        right: 15px;
        z-index: 1;
        cursor: pointer;

        i {
          font-size: 18px;
          color: $tertiary-text-color;
        }
      }

      .title {
        color: $primary-color;
      }

      .icon-wrapper {
        border-color: $title-text-color;
      }
    }

    .value {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 40px;
      padding: 3px 0 2px 0;
      align-items: flex-start;

      .title {
        font-weight: 900;
        font-size: 12px;
        line-height: 15px;
        font-family: "Lato-Bold", sans-serif;
      }

      .text {
        font-family: "Lato-Regular", sans-serif;
        font-size: 15px;
        color: $main-text-color;
      }
    }

    .icon-wrapper {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid $primary-grey;
      border-radius: 4px;
    }

    .opts-list {
      width: 100%;
      z-index: 1;
      position: absolute;
      margin-top: 15px;

      max-height: 400px;
      overflow-y: auto;

      li {
        padding: 15px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .item-left {
          cursor: pointer;

          /*on hover*/
          &:hover {
            .item-inner-wrapper {
              opacity: 1;
              .icon-wrapper {
                border: 1px solid $secondary-text-color;
              }
            }
            /deep/ .check {
              border: 1px solid $primary-color;
            }
          }

          &,
          .item-inner-wrapper {
            display: flex;
            align-items: center;
          }

          .item-inner-wrapper {
            opacity: 0.5;

            .title {
              color: $primary-grey;
            }

            &.active {
              opacity: 1;

              .title {
                color: $primary-color;
              }

              .icon-wrapper {
                border-color: $secondary-text-color;
              }
            }
          }

          .radio-wrapper {
            margin-right: 5px;
          }
        }

        &:last-child {
          margin-bottom: 0;
        }

        .remove-item {
          cursor: pointer;
        }

        &.active {
          .radio-wrapper {
            margin-right: 70px;
          }
        }
      }

      .overlap {
        width: 100%;
        height: 400px;
        position: absolute;
        z-index: 1;
        background: rgba(255, 255, 255, 0.8);
        top: 0;
        left: 0;
      }
    }

    &.opened {
      .chevron {
        i {
          font-size: 20px;
        }
      }
    }

    /deep/ .billing-select-loader {
      width: 30px;
      height: $input-height;

      svg {
        height: 40px;
      }
    }

    .additionalTitle {
      color: $primary-grey;
      font-size: 14px;
      margin-left: 0.5em;
    }
  }
}
</style>
